import React from 'react';
import { Gavel, Building2, BookOpen, RotateCcw, TrendingDown, Users, Truck } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { useTranslation } from '../../i18n';
import MarkdownLite from '../MarkdownLite';

/**
 * 3-act Game Over cinematic shown when the company is liquidated.
 *   Acte 1 — Convocation au Tribunal de Commerce (sobre, gris)
 *   Acte 2 — Liquidation judiciaire (graphique des actifs vendus + chauffeurs)
 *   Acte 3 — Bilan & Leçons (synthèse pédagogique + tips de Maurice)
 * Final CTA: "Recommencer une nouvelle partie".
 */
export default function GameOverModal() {
  const gameOver = useGameStore(s => s.gameOverState);
  const advance = useGameStore(s => s.advanceGameOverStage);
  const restart = useGameStore(s => s.restartAfterLiquidation);
  const { t } = useTranslation();

  if (!gameOver) return null;

  const { stage, trigger, liquidationDay, summary } = gameOver;

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[120] flex items-center justify-center p-4 backdrop-blur-md"
      data-testid="gameover-modal-overlay"
    >
      <div
        className="bg-ink-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto border border-slate-700"
        data-testid="gameover-modal"
      >
        {stage === 1 && <Act1Convocation trigger={trigger} liquidationDay={liquidationDay} onNext={advance} t={t} />}
        {stage === 2 && <Act2Liquidation summary={summary} onNext={advance} t={t} />}
        {stage === 3 && <Act3PostMortem summary={summary} onRestart={restart} t={t} />}
      </div>
    </div>
  );
}

function Act1Convocation({ trigger, liquidationDay, onNext, t }) {
  const reason = trigger === 'technical'
    ? t('gameover_reason_technical')
    : t('gameover_reason_default');
  return (
    <div className="p-10 space-y-6" data-testid="gameover-act-1">
      <div className="flex items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-slate-800 border-2 border-slate-600 flex items-center justify-center">
          <Gavel className="w-10 h-10 text-slate-400" />
        </div>
      </div>
      <div className="text-center space-y-2">
        <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono">
          {t('gameover_court_header')}
        </p>
        <h1 className="text-3xl font-bold text-slate-100 tracking-tight" data-testid="gameover-act1-title">
          {t('gameover_act1_title')}
        </h1>
        <p className="text-xs text-slate-500 font-mono">{t('gameover_day_label', { day: liquidationDay })}</p>
      </div>

      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6 space-y-4 text-sm text-slate-300 leading-relaxed">
        <p>
          <strong className="text-slate-100">{t('gameover_salutation')}</strong>
        </p>
        <p>
          <MarkdownLite as="span" text={t('gameover_act1_p1_pre')} /> <strong className="text-rose-300">{reason}</strong><MarkdownLite text={t('gameover_act1_p1_post')} />
        </p>
        <p>
          <MarkdownLite as="span" text={t('gameover_act1_p2')} />
        </p>
        <p className="text-xs text-slate-500 italic pt-2 border-t border-slate-800">
          {t('gameover_act1_footer')}
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onNext}
          className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100 font-bold text-sm transition-colors"
          data-testid="gameover-act1-next"
        >
          {t('gameover_act1_next')}
        </button>
      </div>
    </div>
  );
}

function Act2Liquidation({ summary, onNext, t }) {
  const { durationDays, fleetSold, driversDismissed, finalDebt, finalEquity } = summary;
  return (
    <div className="p-8 space-y-6" data-testid="gameover-act-2">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/20 border border-rose-500/40">
          <TrendingDown className="w-4 h-4 text-rose-300" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-rose-200">{t('gameover_act2_label')}</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-100 mt-2" data-testid="gameover-act2-title">
          {t('gameover_act2_title')}
        </h1>
      </div>

      {/* Liquidation timeline */}
      <div className="space-y-3">
        <LiquidationRow
          icon={<Truck className="w-5 h-5 text-amber-300" />}
          label={t('gameover_fleet_sold_label')}
          value={t('gameover_fleet_sold_value', { count: fleetSold, plural: fleetSold > 1 ? t('gameover_plural_s') : '' })}
          testId="gameover-fleet-sold"
        />
        <LiquidationRow
          icon={<Users className="w-5 h-5 text-purple-300" />}
          label={t('gameover_drivers_dismissed_label')}
          value={t('gameover_drivers_dismissed_value', { count: driversDismissed, plural: driversDismissed > 1 ? t('gameover_plural_s') : '' })}
          testId="gameover-drivers-dismissed"
        />
        <LiquidationRow
          icon={<Building2 className="w-5 h-5 text-rose-300" />}
          label={t('gameover_residual_debt_label')}
          value={`${finalDebt.toLocaleString('fr-FR')} €`}
          testId="gameover-final-debt"
        />
      </div>

      <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-5">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('gameover_activity_duration')}</div>
            <div className="text-2xl font-bold text-slate-100 font-mono" data-testid="gameover-duration">
              {durationDays} {t('gameover_days')}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{t('gameover_final_equity')}</div>
            <div className="text-2xl font-bold text-rose-300 font-mono" data-testid="gameover-final-equity">
              {finalEquity.toLocaleString('fr-FR')} €
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500 italic text-center">
        {t('gameover_act2_footer')}
      </p>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onNext}
          className="px-6 py-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-sm transition-colors"
          data-testid="gameover-act2-next"
        >
          {t('gameover_act2_next')}
        </button>
      </div>
    </div>
  );
}

function LiquidationRow({ icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-ink-800/60 border border-ink-700" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg bg-ink-700/60 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm text-slate-100 font-medium">{value}</div>
      </div>
    </div>
  );
}

function Act3PostMortem({ summary, onRestart, t }) {
  const { durationDays, totalRevenue, totalExpenses, causes, tips } = summary;
  return (
    <div className="p-8 space-y-6" data-testid="gameover-act-3">
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30">
          <BookOpen className="w-4 h-4 text-emerald-300" />
          <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-200">{t('gameover_act3_label')}</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-100 mt-2" data-testid="gameover-act3-title">
          {t('gameover_act3_title')}
        </h1>
        <p className="text-sm text-slate-400">
          {t('gameover_act3_summary', {
            days: durationDays,
            revenue: totalRevenue.toLocaleString('fr-FR'),
            expenses: totalExpenses.toLocaleString('fr-FR'),
          })}
        </p>
      </div>

      {/* Causes */}
      <div className="space-y-2">
        <h3 className="text-xs uppercase tracking-wider text-rose-300 font-bold">{t('gameover_three_causes')}</h3>
        {causes.map((cause, i) => (
          <div key={`cause-${cause}`} className="p-3 rounded-lg bg-rose-950/40 border border-rose-500/40" data-testid={`gameover-cause-${i}`}>
            <div className="flex gap-2">
              <span className="text-rose-300 font-bold flex-shrink-0">{i + 1}.</span>
              <p className="text-sm text-slate-100 leading-relaxed">{cause}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tips */}
      <div className="space-y-2">
        <h3 className="text-xs uppercase tracking-wider text-emerald-300 font-bold">{t('gameover_tips_label')}</h3>
        {tips.map((tip, i) => (
          <div key={`tip-${tip}`} className="p-3 rounded-lg bg-emerald-950/40 border border-emerald-500/40" data-testid={`gameover-tip-${i}`}>
            <div className="flex gap-2">
              <span className="text-emerald-300 font-bold flex-shrink-0">→</span>
              <p className="text-sm text-slate-100 leading-relaxed">{tip}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="p-4 rounded-lg bg-blue-950/40 border border-blue-500/40 text-xs text-slate-100 italic">
        <MarkdownLite text={t('gameover_wisdom_bonus')} />
      </div>

      <div className="flex justify-center pt-2">
        <button
          type="button"
          onClick={onRestart}
          className="px-8 py-4 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-base transition-all transform hover:scale-105 shadow-lg flex items-center gap-3"
          data-testid="gameover-restart"
        >
          <RotateCcw className="w-5 h-5" />
          {t('gameover_restart_btn')}
        </button>
      </div>
    </div>
  );
}
